import { CdkDrag } from "@angular/cdk/drag-drop";
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatTable } from "@angular/material/table";
import { GlobalHelpDialogComponent } from "@comunicare/comunicare-dashboard-modals";
import { Tools } from "@comunicare/comunicare-helper";
import { DataType, FHIRHelper, IDateInterval, IFilter, IPatient, WidgetName } from "@comunicare/comunicare-interfaces";
import moment from "moment";
import { Subject, Subscription, merge } from "rxjs";
import { first, switchMap, takeUntil, tap } from "rxjs/operators";
import { Base64Helper } from "src/app/helpers/Base64Helper";
import { HelpData } from "src/app/helpers/helpData";
import { IWidgetComponent } from "src/app/models/widget.interface";
import { HealthcareserviceService } from "src/app/providers/healthcareservice.service";
import { PatientService } from "src/app/providers/patient.service";
import { ResponsiveDialogService } from "src/app/providers/responsive-dialog.service";
import { RuleAlertService } from "src/app/providers/rule-alert.service";
import { SessionService } from "src/app/providers/session.service";
import { UserService } from "src/app/providers/user.service";
import { AlertDetailsComponent } from "../../patient-alerts/alert-details/alert-details.component";
import { LastActivityListDataSource } from "./patient-last-activity-widget-list-datasource";

@Component({
  selector: "app-patient-last-activity-widget",
  templateUrl: "./patient-last-activity-widget.component.html",
  styleUrls: ["./patient-last-activity-widget.component.scss"],
})
export class PatientLastActivityWidgetComponent implements IWidgetComponent, OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatTable) table: MatTable<IPatient>;
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(CdkDrag, { static: true }) cdkDrag: CdkDrag;

  public widgetName = WidgetName;
  public isDraggable = true;
  public displayedColumns: string[] = ["patient", "date"];
  public dataSource: LastActivityListDataSource;
  public totalCount = 0;
  public loading = true;
  public fromDate: moment.Moment;
  public toDate: moment.Moment;
  private isInitialDateInterval = true;
  public refreshSub: Subscription;
  /** Subject that emits when the component has been destroyed. */
  // eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-denylist, id-match
  private onDestroy$ = new Subject<void>();

  private monitoringIds: string[] = [];
  private availableServicesRefs: string[] = [];

  constructor(
    private patientService: PatientService,
    private responsiveDialog: ResponsiveDialogService,
    private helpData: HelpData,
    protected sessionService: SessionService,
    private userService: UserService,
    private healthcareService: HealthcareserviceService,
    private ruleAlertService: RuleAlertService
  ) {}

  ngOnInit(): void {
    this.userService
      .isAuthorized("dashboard/lastNoDataTransmissionRuleAlert", "GET")
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((value) => {
        if (value === true) {
          // Add "action" if not already present
          if (!this.displayedColumns.includes("action")) {
            this.displayedColumns.push("action");
          }
        } else {
          // Remove "action" if present
          const indexOfAction = this.displayedColumns.indexOf("action");
          if (indexOfAction !== -1) {
            this.displayedColumns.splice(indexOfAction, 1);
          }
        }
      });

    this.dataSource = new LastActivityListDataSource(this.patientService);
    this.initDateFilter();
    this.initData();
    this.refreshSub = this.sessionService.refreshLastActivityWidget.subscribe(() => {
      this.initData();
    });
  }

  ngAfterViewInit(): void {
    this.sort.sortChange.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.paginator.pageIndex = 0;
    });

    merge(this.sort.sortChange, this.paginator.page)
      .pipe(
        tap(() => this.loadData(true)),
        takeUntil(this.onDestroy$)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.refreshSub?.unsubscribe();
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  private initData() {
    if (
      !this.sessionService.currentService ||
      !this.healthcareService.availableServices() ||
      this.healthcareService.availableServices().length < 1
    ) {
      return;
    }
    this.availableServicesRefs = [this.sessionService.currentService?.reference];
    if (this.sessionService.currentService?.display === this.sessionService.allsOption) {
      this.availableServicesRefs = this.healthcareService.availableServices().map((s) => s.asReference.reference);
    }

    if (this.userService.isMonitoringUser) {
      const current = this.sessionService.currentMonitoringService;
      if (current?.reference === this.sessionService.allsOption) {
        this.monitoringIds = this.healthcareService.availableMonitoringServices().map((s) => s.asReference.reference);
      } else {
        this.monitoringIds = current ? [current.reference] : null;
      }
    }
    this.loadData(false);
  }

  private async loadData(noCount: boolean): Promise<void> {
    this.loading = true;
    await this.dataSource.loadData({
      pageNumber: this.paginator?.pageIndex ?? 0,
      pageSize: this.paginator?.pageSize ?? 5,
      filters: this.getFiltersFormatted(),
      sortId: this.sort?.active,
      sortOrder: this.sort?.direction,
      services: this.availableServicesRefs,
      monitoringServicesIds: this.monitoringIds?.length ? this.monitoringIds : undefined,
    });
    if (!noCount) {
      this.totalCount = this.dataSource.getDataCount();
    }
    this.loading = false;
  }

  private getFiltersFormatted(): IFilter[] {
    const filters = Tools.deepCopy(this.dataSource.getAllFilters());
    filters.forEach((f) => {
      if (f.dataType === DataType.DATE) {
        f.data.fromDate = moment(f.data.fromDate).format("YYYY-MM-DD");
        f.data.toDate = moment(f.data.toDate).format("YYYY-MM-DD");
      }
    });
    return filters;
  }

  public getCaremateIdentifier(patient: IPatient): string {
    return FHIRHelper.getMainIdentifier(patient).value;
  }

  public encodeToB64(str: string): string {
    return Base64Helper.utf8_to_b64(str);
  }

  public openInactifPatients(): void {
    this.responsiveDialog.open(
      GlobalHelpDialogComponent,
      {
        data: { slides: this.helpData.inactifPatients },
        disableClose: true,
      },
      { maxWidth: "80vw" }
    );
  }

  /**
   * IFilter
   */
  public getFilter(propertyName: string): IFilter {
    return this.dataSource.getFilter(propertyName);
  }

  public applyFilter(filter: IFilter): void {
    this.dataSource.setFilter(filter);
    if (this.paginator) this.paginator.pageIndex = 0;
    this.loadData(false);
  }

  /** Default date range: from 1 year ago up to now. */
  private initDateFilter(): void {
    this.fromDate = moment().subtract(1, "year").startOf("day");
    this.toDate = moment().endOf("day");
    this.dataSource.setFilter(this.buildDateFilter());
  }

  private buildDateFilter(): IFilter {
    return {
      data: { fromDate: this.fromDate, toDate: this.toDate },
      propertyName: "lastActivity",
      dataType: DataType.DATE,
    };
  }

  /**
   * Triggered by the date interval selector above the table. The selector emits
   * its initial range on init, which we already applied in initDateFilter, so we
   * skip that first (redundant) emit to avoid a duplicate load.
   */
  public onDateIntervalChange(interval: IDateInterval): void {
    if (!interval?.fromDate || !interval?.toDate) {
      return;
    }
    this.fromDate = interval.fromDate;
    this.toDate = interval.toDate;
    if (this.isInitialDateInterval) {
      this.isInitialDateInterval = false;
      return;
    }
    this.applyFilter(this.buildDateFilter());
  }

  public clearFilter(): void {
    this.dataSource.clearFilter();
    if (this.paginator) this.paginator.pageIndex = 0;
    this.loadData(false);
  }

  public onDetails(patient: IPatient, createAction: boolean): void {
    this.ruleAlertService
      .listNoDataTransmission(this.getCaremateIdentifier(patient))
      .pipe(
        first(),
        switchMap((ra) => {
          return this.responsiveDialog
            .open(
              AlertDetailsComponent,
              {
                data: {
                  alert: ra,
                  createAction,
                  patientServiceId: patient.healthcareservice?.[0]?.reference,
                },
              },
              { maxWidth: createAction ? "80vw" : "30vw", maxHeight: "93vh" }
            )
            .afterClosed();
        })
      )
      .subscribe((needReload) => {
        if (needReload) {
          this.loadData(false);
        }
      });
  }
}
